import React, { useMemo } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import type {
  AdminUserActiveThreatHistoryPoint,
  AdminUserRiskHistoryPoint,
} from '../../../gen/client/react/client'

type ChartPoint = {
  actorSub?: string
  id: string
  level: string
  recordedAt: string
  score: number
  source: string
  signal: 'Risk' | 'Active threat'
}

export type UserSecurityHistoryChartProps = {
  riskPoints: AdminUserRiskHistoryPoint[]
  activeThreatPoints: AdminUserActiveThreatHistoryPoint[]
  loading?: boolean
  error?: string | null
  onRetry?: () => void
}

const ChartFrame = styled.div`
  display: grid;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

const ChartSvg = styled.svg`
  width: 100%;
  min-height: 260px;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  border: 1px solid
    ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
`

const Legend = styled.ul`
  display: flex;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
  padding: 0;
  margin: 0;
  list-style: none;
`

const LegendItem = styled.li<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};

  &::before {
    width: 1.5rem;
    height: 0.25rem;
    content: '';
    background: ${({ $color }) => $color};
    border-radius: 999px;
  }
`

const StateBox = styled.div`
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  background: ${({ theme }) => asUnified(theme).bg?.subtle ?? '#f8fafc'};
  border: 1px solid
    ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
`

const RetryButton = styled.button`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  margin-left: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font: inherit;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  cursor: pointer;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  border: 1px solid
    ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
`

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: ${({ theme }) => ut(asUnified(theme)).space('xs')};
    text-align: left;
    border-bottom: 1px solid
      ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  }

  th {
    font-weight: 600;
  }
`

const riskColor = '#2563eb'
const threatColor = '#dc2626'
const chartWidth = 720
const chartHeight = 260
const padLeft = 52
const padRight = 20
const padTop = 20
const padBottom = 44

function finiteScore(score: number): number {
  if (!Number.isFinite(score)) return 0
  return Math.max(0, Math.min(1, score))
}

function toMillis(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function displayTimestamp(value: string): string {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

function displayScore(score: number): string {
  return finiteScore(score).toFixed(9).replace(/0+$/, '').replace(/\.$/, '')
}

function chartCoordinates(
  points: ChartPoint[],
  minTime: number,
  maxTime: number
): Array<{ point: ChartPoint; x: number; y: number }> {
  const plotWidth = chartWidth - padLeft - padRight
  const plotHeight = chartHeight - padTop - padBottom
  const span = Math.max(1, maxTime - minTime)
  const hasTimeSpan = maxTime > minTime
  return [...points]
    .sort((a, b) => toMillis(a.recordedAt) - toMillis(b.recordedAt))
    .map((point) => {
      const x = hasTimeSpan
        ? padLeft + ((toMillis(point.recordedAt) - minTime) / span) * plotWidth
        : padLeft + plotWidth / 2
      const y = padTop + (1 - finiteScore(point.score)) * plotHeight
      return { point, x, y }
    })
}

export const UserSecurityHistoryChart = ({
  riskPoints,
  activeThreatPoints,
  loading = false,
  error = null,
  onRetry,
}: UserSecurityHistoryChartProps): JSX.Element => {
  const allPoints = useMemo<ChartPoint[]>(
    () =>
      [
        ...riskPoints.map((point) => ({ ...point, signal: 'Risk' as const })),
        ...activeThreatPoints.map((point) => ({
          ...point,
          signal: 'Active threat' as const,
        })),
      ].sort((a, b) => toMillis(b.recordedAt) - toMillis(a.recordedAt)),
    [riskPoints, activeThreatPoints]
  )

  if (loading) {
    return <StateBox role="status">Loading security history…</StateBox>
  }
  if (error) {
    return (
      <StateBox role="alert">
        Unable to load security history: {error}
        {onRetry && (
          <RetryButton type="button" onClick={onRetry}>
            Retry
          </RetryButton>
        )}
      </StateBox>
    )
  }
  if (allPoints.length === 0) {
    return (
      <StateBox role="status">
        No risk or active-threat changes were recorded in this period.
      </StateBox>
    )
  }

  const times = allPoints.map((point) => toMillis(point.recordedAt))
  const minTime = Math.min(...times)
  const maxTime = Math.max(...times)
  const riskCoordinates = chartCoordinates(
    riskPoints.map((point) => ({ ...point, signal: 'Risk' })),
    minTime,
    maxTime
  )
  const threatCoordinates = chartCoordinates(
    activeThreatPoints.map((point) => ({
      ...point,
      signal: 'Active threat',
    })),
    minTime,
    maxTime
  )
  const riskLine = riskCoordinates
    .map(({ x, y }) => `${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ')
  const threatLine = threatCoordinates
    .map(({ x, y }) => `${x.toFixed(2)},${y.toFixed(2)}`)
    .join(' ')
  const plotHeight = chartHeight - padTop - padBottom
  const yTicks = [0, 0.25, 0.5, 0.75, 1]

  return (
    <ChartFrame>
      <Legend aria-label="Chart legend">
        <LegendItem $color={riskColor}>Risk</LegendItem>
        <LegendItem $color={threatColor}>Active threat</LegendItem>
      </Legend>
      <ChartSvg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        role="img"
        aria-labelledby="security-history-title security-history-description"
      >
        <title id="security-history-title">Account security history</title>
        <desc id="security-history-description">
          Normalized risk and active-threat scores from zero to one over time.
          Exact values are available in the table following the chart.
        </desc>
        {yTicks.map((tick) => {
          const y = padTop + (1 - tick) * plotHeight
          return (
            <g key={tick}>
              <line
                x1={padLeft}
                x2={chartWidth - padRight}
                y1={y}
                y2={y}
                stroke="#cbd5e1"
                strokeWidth="1"
              />
              <text x={padLeft - 10} y={y + 4} textAnchor="end" fontSize="12">
                {tick.toFixed(2)}
              </text>
            </g>
          )
        })}
        <text
          x={16}
          y={chartHeight / 2}
          textAnchor="middle"
          fontSize="12"
          transform={`rotate(-90 16 ${chartHeight / 2})`}
        >
          Normalized score
        </text>
        <text x={padLeft} y={chartHeight - 12} textAnchor="start" fontSize="12">
          {displayTimestamp(new Date(minTime).toISOString())}
        </text>
        <text
          x={chartWidth - padRight}
          y={chartHeight - 12}
          textAnchor="end"
          fontSize="12"
        >
          {displayTimestamp(new Date(maxTime).toISOString())}
        </text>
        {riskLine && (
          <polyline
            aria-label="Risk score series"
            points={riskLine}
            fill="none"
            stroke={riskColor}
            strokeWidth="3"
            strokeLinejoin="round"
          />
        )}
        {riskCoordinates.map(({ point, x, y }) => (
          <circle key={point.id} cx={x} cy={y} r="4" fill={riskColor}>
            <title>{`Risk ${point.level}, ${finiteScore(point.score).toFixed(3)}, ${displayTimestamp(point.recordedAt)}`}</title>
          </circle>
        ))}
        {threatLine && (
          <polyline
            aria-label="Active-threat score series"
            points={threatLine}
            fill="none"
            stroke={threatColor}
            strokeWidth="3"
            strokeLinejoin="round"
          />
        )}
        {threatCoordinates.map(({ point, x, y }) => (
          <circle key={point.id} cx={x} cy={y} r="4" fill={threatColor}>
            <title>{`Active threat ${point.level}, ${finiteScore(point.score).toFixed(3)}, ${displayTimestamp(point.recordedAt)}`}</title>
          </circle>
        ))}
      </ChartSvg>
      <DataTable>
        <caption>Exact account security history values</caption>
        <thead>
          <tr>
            <th scope="col">Recorded</th>
            <th scope="col">Signal</th>
            <th scope="col">Level</th>
            <th scope="col">Score</th>
            <th scope="col">Source</th>
            <th scope="col">Actor</th>
          </tr>
        </thead>
        <tbody>
          {allPoints.map((point) => (
            <tr key={`${point.signal}-${point.id}`}>
              <td>{displayTimestamp(point.recordedAt)}</td>
              <td>{point.signal}</td>
              <td>{point.level}</td>
              <td>{displayScore(point.score)}</td>
              <td>{point.source.replaceAll('_', ' ')}</td>
              <td>{point.actorSub || 'System'}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </ChartFrame>
  )
}
